import { getText } from 'src/ConsentManagement/TranslationService';
import { Accordion } from '../shared/Accordion';
import { AccordionContent } from '../shared/Accordion/AccordionContent';
import { AccordionHeader } from '../shared/Accordion/AccordionHeader';
import { TotalOptedIn } from '../shared/ConsentWidget/TotalOptedIn';
import { Icon } from '../shared/Icon';
import { TriStateCheckbox, ListOptionState } from '../shared/UiStates/TriStateCheckbox';
import { UseVendorSelections } from '../shared/UiStates/UseVendorSelections';
import { InfoListItem, PrivacyDetail } from './PrivacyDetail';
import { VendorModel, VendorSelectionType } from './vendorTypes';

const getDisplayUrl = (urlString: string) => {
  const url = urlString.replace(/(^\w+:|^)\/\//, '');
  return `${url}`;
};

const EmptyVendorList = ({ vendorCount }: { vendorCount: number }) => (vendorCount != 0 ? null : <div>loading...</div>);

const getFriendlyTimespan = (seconds: number) => {
  const d = Math.floor(seconds / (3600 * 24));
  const h = Math.floor((seconds % (3600 * 24)) / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  const s = Math.floor(seconds % 60);

  const display = (num: number, translateKey: string) => (num != 0 ? `${num} ${getText(`time_${translateKey}`)} ` : '');

  return `${display(d, 'days')}${display(h, 'hrs')}${display(m, 'mins')}${display(s, 'secs')}`;
};

const Vendor = ({ vendor }: { vendor: VendorModel }) => {
  const { setVendor } = UseVendorSelections();
  const uid = `vendor_${vendor.vendorId}_${vendor.isIabVendor ? 'iab' : 'notiab'}`;

  const onVendorToggleClicked = () => {
    const newVendor = new VendorModel(vendor);
    
    const currentState = newVendor.getToggleState() == ListOptionState.none;
    newVendor.selections = currentState ? newVendor.getToggleOptions() : [];

    setVendor(newVendor);
  };

  const onVendorOptionClicked = (selectionType: VendorSelectionType) => {
    const newVendor = new VendorModel(vendor).ToggleSelection(selectionType);
    setVendor(newVendor);
  };

  return (
    <Accordion uid={uid}>
      <AccordionHeader
        uid={uid}
        title={vendor.name}
        titleIcon={
          vendor.isIabVendor ? null : (
            <div class="icon-other-vendor">
              <Icon variant={'other-vendors'} />
            </div>
          )
        }
        afterTitleComponents={
          <TotalOptedIn
            totalOptions={vendor.getToggleOptions().length}
            optedIn={vendor.selections?.length ?? 0}
            isEssential={vendor.isEssential ?? false}
          />
        }
        endComponents={
          vendor.isEssential ? (
            <div class="vendor-essential-spacer"></div>
          ) : (
            <>
              <hr class="cc-accordion-separator" />
              <TriStateCheckbox id={uid} toggleState={vendor.getToggleState()} changeHandler={onVendorToggleClicked} />
            </>
          )
        }
      ></AccordionHeader>

      <AccordionContent>
        <PrivacyDetail
          uid={uid}
          titleTranslationKey={'purposes'}
          translationKey={'purpose'}
          detailIds={vendor.purposeIds}
          optionModel={
            vendor.isEssential
              ? null
              : {
                  isSelected: vendor.isSelected(VendorSelectionType.Purposes),
                  vendorOptionType: VendorSelectionType.Purposes,
                  onOptionClicked: onVendorOptionClicked,
                }
          }
        />

        <PrivacyDetail
          uid={uid}
          titleTranslationKey={'legIntPurposes'}
          translationKey={'legIntPurpose'}
          detailIds={vendor.legIntPurposeIds}
          optionModel={
            vendor.isEssential
              ? null
              : {
                  isSelected: vendor.isSelected(VendorSelectionType.LegitimateInterest),
                  vendorOptionType: VendorSelectionType.LegitimateInterest,
                  onOptionClicked: onVendorOptionClicked,
                }
          }
        />

        <PrivacyDetail titleTranslationKey={'features'} translationKey={'feature'} detailIds={vendor.featureIds} />

        <PrivacyDetail
          titleTranslationKey={'specialFeatures'}
          translationKey={'special_feature'}
          detailIds={vendor.specialFeatureIds}
        />

        <PrivacyDetail
          translationKey={'special_purpose'}
          titleTranslationKey={'specialPurposes'}
          detailIds={vendor.specialPurposeIds}
        />

        {vendor.isIabVendor ? (
          <InfoListItem titleTranslationKey={'maxCookieAge'}>
            {getFriendlyTimespan(vendor.cookieMaxAgeSeconds)}
          </InfoListItem>
        ) : null}

        <InfoListItem titleTranslationKey={'privacyPolicy'}>
          <a href={vendor.policyUrl} class={'vendor-privacy-link'} target="_blank">
            {getDisplayUrl(vendor.policyUrl)}
          </a>
        </InfoListItem>
      </AccordionContent>
    </Accordion>
  );
};

export const VendorsList = () => {
  const { vendorSelections } = UseVendorSelections();

  return (
    <section>
      <EmptyVendorList vendorCount={vendorSelections.Total()} />
      {vendorSelections.models.map((vendor) => (
        <Vendor vendor={vendor} />
      ))}
    </section>
  );
};
